import React, { useState }  from 'react';
import { IWebsiteWidgetConfig, WebsiteWidgetLayout } from '../../interfaces/website_widget';
import WebsiteWidgetConfig from '../../components/widgets/website_widget/website_widget_config';
import { Form } from '../../components/common/form';
import { FormRadioGroup } from '../../components/common/form_radio_group';
import WebsiteWidgetPreview from './website_widget_preview';
import WebsiteWidgetCode from './website_widget_code';
import { ISessionState, SessionType } from '../../interfaces/session';
import { useSelector } from 'react-redux';
import PageCommunitySelectWrapper from '../../components/common/page_community_select_wrapper';
import WebsiteWidgetConfigTable from '../../components/widgets/website_widget/website_widget_config_table';

const WebsiteWidget = () => {
    const sessionState: ISessionState = useSelector(
        ({ SessionState }: any) => SessionState
    );

    const defaultConfig: IWebsiteWidgetConfig = {
        layout: WebsiteWidgetLayout.Vertical,
        nhid: sessionState.Session.SessionType === SessionType.SessionTypeOrg
            ? sessionState.Org.NHID
            : sessionState?.Properties[0]?.NHID,
        showReviewScore: true,
        showRatings: true,
        showWriteReviewButton: true,
        showReadAllReviewsLink: true,
        widthFull: true,
        reviewCount: 10,
        showLogo: true,
        backgroundColor: 'light-blue',
        showFacilityName: true,
        showReviews: true,
        slideShow: true,
        slideShowReviewsCount: 2,
        showDate: true,
        showReviewSnippet: true,
        showReviewerAndConnection: true,
        border: true,
        noFollowLinks: false,
        minReviewRating: null,
        marginTop: 10,
        marginBottom: 10,
        marginLeft: 10,
        marginRight: 10
    };

    const [script, setScript] = useState<HTMLScriptElement>(null);
    const [refreshPreview, setRefreshPreview] = useState<boolean>(false);

    const handleSubmit = async (data: IWebsiteWidgetConfig) => {
        console.log(data);
    };

    return (
        <section>
            <div className="container max-w-none 2xl:container mt-4 sm:mt-6 md:mt-8 2xl:mt-10">
                <Form
                    defaultValues={defaultConfig}
                    onSubmit={handleSubmit}
                    onChange={() => setRefreshPreview(true)}
                >
                    {
                        ({ watch, setValue }) => {
                            const layout = watch('layout');

                            return (
                                <>
                                    <PageCommunitySelectWrapper
                                        handleSelect={(nhid: number) => {
                                            console.log(nhid);
                                            setValue('nhid', nhid);
                                            setRefreshPreview(true);
                                        }}
                                        selNHID={watch('nhid')}
                                    />
                                    <div className="mt-4 sm:mt-6 md:mt-8 2xl:mt-10 grid gap-3 lg:gap-4 xl:gap-8">
                                        <div className="ring-1 bg-brand_faint-blue p-3 sm:p-4 xl:px-6 ring-brand_grey rounded-md sm:rounded-lg overflow-hidden flex flex-wrap items-center gap-4 lg:gap-10">
                                            <h2 className="text-md md:text-lg lg:text-xl font-bold flex items-center">
                                                <span className="px-3 py-2 rounded-xl bg-brand_primary text-white flex items-center justify-center text-xl font-bold me-2 min-w-20 whitespace-nowrap">Step 1</span> Choose Widget Type
                                            </h2>
                                            <div className="flex flex-wrap items-center gap-y-2 gap-x-6 [&_label]:bg-white [&_label]:px-4 [&_label]:py-2 [&_label]:rounded-full [&_label]:font-semibold [&_label>svg]:left-4">
                                                <FormRadioGroup
                                                    name="layout"
                                                    options={[
                                                        {
                                                            label: 'Vertical Review Widget',
                                                            value: WebsiteWidgetLayout.Vertical
                                                        },
                                                        {
                                                            label: 'Horizontal Review Widget',
                                                            value: WebsiteWidgetLayout.Horizontal,
                                                        }
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                        <div className="grid gap-3 lg:gap-4 xl:gap-8 lg:grid-cols-12 items-start">
                                            { layout === WebsiteWidgetLayout.Vertical ?
                                                <>
                                                    <div className="grid gap-3 lg:gap-4 xl:gap-8 lg:col-span-7">
                                                        <WebsiteWidgetConfig
                                                            config={watch()}
                                                            setRefreshPreview={setRefreshPreview}
                                                        /> 
                                                        <WebsiteWidgetCode
                                                            embedCode={script?.outerHTML}
                                                        />
                                                    </div>
                                                    <div className="lg:col-span-5 h-full">
                                                        <WebsiteWidgetPreview 
                                                            config={watch()}
                                                            script={script}
                                                            setScript={setScript}
                                                            refreshPreview={refreshPreview}
                                                            setRefreshPreview={setRefreshPreview}
                                                        />
                                                    </div>
                                                </>
                                                :
                                                <>
                                                    <div className="md:order-2 lg:col-span-6">
                                                        <WebsiteWidgetConfig
                                                            config={watch()}
                                                            setRefreshPreview={setRefreshPreview}
                                                        /> 
                                                    </div>
                                                    <div className="md:order-3 lg:col-span-6 lg:sticky lg:top-4 xl:top-8">
                                                        <WebsiteWidgetCode
                                                            embedCode={script?.outerHTML}
                                                        />
                                                    </div>
                                                    <div className="md:order-1 lg:col-span-12 [&>div]:min-h-[400px]">
                                                        <WebsiteWidgetPreview 
                                                            config={watch()}
                                                            script={script}
                                                            setScript={setScript}
                                                            refreshPreview={refreshPreview}
                                                            setRefreshPreview={setRefreshPreview}
                                                        />
                                                    </div>
                                                </>
                                            }
                                        </div>
                                    </div>
                                </>
                            );
                        }
                    }
                </Form>
                <div className="mt-12">
                    <h2 className="mb-2 text-2xl font-bold">Parameter Details</h2>
                    <p className="mb-4">If you wish to manually or programmatically alter a widget URL please refer to the table below for the widget parameters.</p>
                    <WebsiteWidgetConfigTable />
                </div>
            </div>
        </section>
    ); 
};

export default WebsiteWidget;
